<script setup>
    import { useStageStore } from '@/stores/stage';
    import { computed, ref, watch, nextTick, onMounted } from "vue";
    import { useRoute } from 'vue-router';

    const formStorage = useStageStore();
    const route = useRoute();

    const stageComponents = computed(() => {
        return formStorage.getStageById(route.params.id)[0].node.components[0];
    })

    const stageAnswers = ref({
        id: route.params.id,
        answer: [],
        index: undefined,
        locale: undefined
    })

    const choicesData = ref();

    const updateStageAnswers = (index) => {
        formStorage.manageAnswers(route.params.id, stageAnswers.value.answer, index)
        formStorage.managePointsByStage(route.params.id)
        formStorage.updateStageAccessStatus(formStorage.stagesData.data.stages)
        formStorage.managePointsByPathType()
    }

    const choicesDetails = (route) => {
        // Get choices details
        choicesData.value = formStorage.getChoicesDetails(route)
    }

    const buttonClick = () => {
        formStorage.manageAnswers(route.params.id, "clicked")
        formStorage.updateStageAccessStatus(formStorage.stagesData.data.stages)
        formStorage.managePointsByPathType()
    }

    onMounted(() => {
        const answersByStage = formStorage.getAllAnswersByStageId(route.params.id)

        if(answersByStage !== undefined){
            Object.assign(stageAnswers.value, answersByStage);
        }

        // Calculate progress
        formStorage.calculateSurveyProgressInPercents()

        // Manage total points in pinia
        formStorage.manageTotalPoints()

        // Choices content
        choicesDetails(route.params.id)
  	})

    // Manage answers on route change
    watch(route, (from, to) => {
        nextTick(() => {
            const answersByStage = formStorage.getAllAnswersByStageId(route.params.id)

            if(answersByStage !== undefined){
                // If answer has been given already, load answers into view
                Object.assign(stageAnswers.value, answersByStage);
            }
            else {
                // If answer is not given, prepare a placeholder answer object
                const obj = {
                    id: null,
                    answer: [],
                    index: null,
                    locale: undefined
                }

                stageAnswers.value = obj
            }

            // Choices content
            choicesDetails(route.params.id)
        })
    })
</script>

<template>
    <div v-if="stageComponents">
        <fieldset v-if="stageComponents.type == 'radio' || stageComponents.type == 'choices'">
            <legend>{{ stageComponents.label[formStorage.language] }}</legend>

            <div v-for="(option, index) in stageComponents.options" :key="index" class="form-check">
                <input
                    :id="`option_${formStorage.stage}_${index}`"
                    type="radio" :value="option.title[formStorage.language]"
                    name="`radio`"
                    v-model="stageAnswers.answer"
                    v-on:change="updateStageAnswers(index); choicesDetails(route.params.id);"
                    class="form-check-input"
                />

                <label :for="`option_${formStorage.stage}_${index}`" class="form-check-label">
                    {{ option.title[formStorage.language] }}
                </label>
            </div>
        </fieldset>

        <div 
            v-if="choicesData && stageComponents.type !== 'doneByDefault'" 
            :class="`choice_${choicesData.type}`"
        >
            <p>{{ choicesData.content }}</p>
        </div>

        <fieldset v-if="stageComponents.type == 'checkbox'">
            <legend>{{ stageComponents.label[formStorage.language] }}</legend>

            <div v-for="(option, index) in stageComponents.options" :key="index" class="form-check">
                <input
                    :id="`option_${formStorage.stage}_${index}`"
                    :type="stageComponents.type"
                    :value="option.title[formStorage.language]"
                    v-model="stageAnswers.answer"
                    v-on:change="updateStageAnswers();"
                    class="form-check-input"
                />
                <label :for="`option_${formStorage.stage}_${index}`" class="form-check-label">{{ option.title[formStorage.language] }}</label>
            </div>
        </fieldset>

        <fieldset class="mb-3 input-group" v-if="stageComponents.type == 'textarea'">
            <legend :id="`textarea_${formStorage.stage}`">{{ stageComponents.label[formStorage.language] }}</legend>
            <textarea
                :aria-labelledby="`textarea_${formStorage.stage}`"
                class="form-control"
                v-model="stageAnswers.answer"
                v-on:input="updateStageAnswers()"
            >
            </textarea>
        </fieldset>

        <button
            v-if="stageComponents.type == 'button'"
            class="btn btn-outline-dark"
            value="true"
            v-on:click="buttonClick()">

            <span v-if="formStorage.getAllAnswersByStageId(route.params.id)?.answer.length > 0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0V0z" fill="none"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z"/>
                </svg>
            </span>
            {{ stageComponents.label[formStorage.language] }}
        </button>

        <div class="done-by-default" v-if="stageComponents.type == 'doneByDefault'"></div>
    </div>
</template>